/**
 * 剪貼簿工具函數
 * 提供統一的複製到剪貼簿功能
 *
 * 使用方式：
 *
 * 1. 基本使用（Promise-based）：
 *    import { copyToClipboard } from 'utils';
 *    await copyToClipboard('text to copy');
 *
 * 2. 帶自訂回調：
 *    await copyToClipboard('text', {
 *      onSuccess: () => console.log('Copied!'),
 *      onError: (err) => console.error('Failed:', err)
 *    });
 *
 * 3. React Hook：
 *    import { useCopyToClipboard } from 'utils';
 *    const { copyToClipboard, isCopied } = useCopyToClipboard();
 *
 * 4. 全域函數（用於 HTML onclick 等）：
 *    <button onclick="window.copyToClipboard('text')">Copy</button>
 */

import { useState, useCallback } from 'react';

/**
 * 核心複製函數
 * 支援現代 Clipboard API 和舊瀏覽器 fallback
 *
 * @param {string} text - 要複製的文字
 * @param {Object} options - 選項
 * @param {Function} options.onSuccess - 成功回調
 * @param {Function} options.onError - 失敗回調
 * @param {boolean} options.silent - 是否靜默（不執行回調）
 * @returns {Promise<boolean>} - 是否成功
 */
export async function copyToClipboard(text, options = {}) {
  const { onSuccess, onError, silent = false } = options;

  try {
    // 優先使用現代 Clipboard API
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      if (!silent && onSuccess) {
        onSuccess(text);
      }
      return true;
    }

    // Fallback：使用 execCommand（支援舊瀏覽器）
    return fallbackCopyToClipboard(text, { onSuccess, onError, silent });
  } catch (err) {
    console.error('Clipboard copy failed:', err);

    // 如果 Clipboard API 失敗，嘗試 fallback
    return fallbackCopyToClipboard(text, { onSuccess, onError, silent });
  }
}

/**
 * Fallback 複製方法（使用 document.execCommand）
 * 用於不支援 Clipboard API 的舊瀏覽器
 *
 * @param {string} text - 要複製的文字
 * @param {Object} options - 選項
 * @returns {boolean} - 是否成功
 */
function fallbackCopyToClipboard(text, options = {}) {
  const { onSuccess, onError, silent = false } = options;

  // 創建臨時 textarea 元素
  const textArea = document.createElement('textarea');
  textArea.value = text;

  // 設置樣式使其不可見
  textArea.style.position = 'fixed';
  textArea.style.top = '0';
  textArea.style.left = '0';
  textArea.style.width = '2em';
  textArea.style.height = '2em';
  textArea.style.padding = '0';
  textArea.style.border = 'none';
  textArea.style.outline = 'none';
  textArea.style.boxShadow = 'none';
  textArea.style.background = 'transparent';

  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    const successful = document.execCommand('copy');
    if (successful) {
      if (!silent && onSuccess) {
        onSuccess(text);
      }
      return true;
    } else {
      throw new Error('execCommand failed');
    }
  } catch (err) {
    console.error('Fallback copy failed:', err);
    if (!silent && onError) {
      onError(err);
    }
    return false;
  } finally {
    document.body.removeChild(textArea);
  }
}

/**
 * React Hook: 複製到剪貼簿
 * 提供狀態管理和自動重置
 *
 * @param {number} resetDelay - 重置延遲（毫秒），默認 2000ms
 * @returns {Object} { copyToClipboard, isCopied, error }
 *
 * @example
 * function MyComponent() {
 *   const { copyToClipboard, isCopied } = useCopyToClipboard();
 *
 *   return (
 *     <button onClick={() => copyToClipboard('Hello')}>
 *       {isCopied ? 'Copied!' : 'Copy'}
 *     </button>
 *   );
 * }
 */
export function useCopyToClipboard(resetDelay = 2000) {
  const [isCopied, setIsCopied] = useState(false);
  const [error, setError] = useState(null);

  const copy = useCallback(async (text) => {
    try {
      const success = await copyToClipboard(text, { silent: true });

      if (success) {
        setIsCopied(true);
        setError(null);

        // 自動重置狀態
        setTimeout(() => {
          setIsCopied(false);
        }, resetDelay);

        return true;
      } else {
        throw new Error('Copy failed');
      }
    } catch (err) {
      console.error('Copy error:', err);
      setError(err);
      setIsCopied(false);
      return false;
    }
  }, [resetDelay]);

  return {
    copyToClipboard: copy,
    isCopied,
    error
  };
}

/**
 * 從 input 元素複製文字
 * 便捷函數，用於直接從 input/textarea 複製
 *
 * @param {string} elementId - 元素 ID
 * @param {Object} options - 選項
 * @returns {Promise<boolean>} - 是否成功
 */
export async function copyFromElement(elementId, options = {}) {
  const element = document.getElementById(elementId);

  if (!element) {
    console.error(`Element with id "${elementId}" not found`);
    if (options.onError) {
      options.onError(new Error('Element not found'));
    }
    return false;
  }

  const text = element.value || element.textContent || element.innerText;
  return copyToClipboard(text, options);
}

/**
 * 暴露全域函數
 * 用於 HTML onclick 等場景
 *
 * 注意：這個函數會自動在模組載入時執行
 */
if (typeof window !== 'undefined') {
  // 全域複製函數（簡化版，用於 onclick）
  window.copyToClipboard = async function(text, showNotification = true, customMessage = null) {
    const success = await copyToClipboard(text, { silent: true });

    if (showNotification && success) {
      // Get translated message from calComSettings if available
      const message = customMessage ||
                     (window.calComSettings?.translations?.cal_com_settings?.clipboard_copied) ||
                     '已複製到剪貼簿';

      // 如果頁面上有 calComSettings 實例，使用其通知系統
      if (window.calComSettings && typeof window.calComSettings.showNotification === 'function') {
        window.calComSettings.showNotification(message, 'success');
      } else {
        // 否則使用簡單的 console 提示
        console.log('✅ ' + message);
      }
    }

    return success;
  };

  // 從元素複製（全域版本）
  window.copyFromElement = async function(elementId, showNotification = true, customMessage = null) {
    return copyFromElement(elementId, {
      onSuccess: showNotification ? () => {
        const message = customMessage ||
                       (window.calComSettings?.translations?.cal_com_settings?.clipboard_copied) ||
                       '已複製到剪貼簿';

        if (window.calComSettings && typeof window.calComSettings.showNotification === 'function') {
          window.calComSettings.showNotification(message, 'success');
        }
      } : undefined
    });
  };
}

export default {
  copyToClipboard,
  useCopyToClipboard,
  copyFromElement
};
